/**
 * /forecasts-graded — public "Our forecasts, graded" trust page.
 *
 * Aggregated, anonymized predicted-vs-actual accuracy across every CARTIEAI
 * workspace. Published from day one — a track record no competitor can copy
 * retroactively.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowRight, ShieldCheck, Scale } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 backdrop-blur-xl p-6" data-testid={testId}>
    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1.5">{label}</div>
    <div className={`text-2xl md:text-3xl font-extrabold tabular-nums leading-tight ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
  </div>
);

const ForecastsGraded = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/decision-ledger/public-grades`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const graded = data?.forecasts_graded || 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="forecasts-graded-page">
      <SEOHead
        title="Our forecasts, graded — public predicted-vs-actual accuracy"
        description="CARTIEAI publishes the accuracy of its own cost-savings forecasts, aggregated across all customers. Predicted vs actual, graded in public, from day one. No FinOps tool has ever dared."
        canonical="https://cartieai.com/forecasts-graded"
        keywords="FinOps forecast accuracy, cost savings prediction accuracy, graded forecasts, transparent FinOps, CARTIEAI trust"
      />
      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <Badge className="bg-sky-500/10 border-sky-400/40 text-sky-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-4">
            <Target className="w-3 h-3 mr-1.5 inline" /> Radical transparency · updates live
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Our forecasts, <span className="text-emerald-400">graded.</span>
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto">
            Every tool makes savings promises. Nobody audits their own. Below is the aggregated,
            anonymized accuracy of every CARTIEAI decision forecast that customers graded against
            reality — published from day one, whether the number flatters us or not.
          </p>
        </div>

        {loaded && graded === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-slate-900/50 backdrop-blur-xl px-8 py-14 text-center" data-testid="fg-empty">
            <Scale className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <div className="text-xl font-bold text-white mb-2">No graded forecasts yet — and that's the point.</div>
            <p className="text-sm text-slate-400 max-w-xl mx-auto">
              This page went live before we had numbers to brag about. As customers approve decisions in the
              Decision Ledger and record real outcomes, the aggregate accuracy appears here automatically —
              unedited, uncurated.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="text-[11px] font-bold tracking-widest text-slate-500 uppercase mb-2">Average forecast accuracy</div>
              <div className="font-display text-7xl sm:text-8xl font-black tabular-nums text-emerald-300 drop-shadow-[0_0_35px_rgba(16,185,129,0.35)]" data-testid="fg-hero-accuracy">
                {data?.avg_accuracy_pct != null ? `${data.avg_accuracy_pct}%` : '—'}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                across {graded} graded decision{graded === 1 ? '' : 's'}
                {data?.last_graded_at && ` · last graded ${new Date(data.last_graded_at).toLocaleDateString()}`}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <Stat label="Forecasts graded" value={graded} sub="approved decisions with real outcomes" testId="fg-stat-count" />
              <Stat label="Median accuracy" value={data?.median_accuracy_pct != null ? `${data.median_accuracy_pct}%` : '—'} color="text-sky-300" testId="fg-stat-median" />
              <Stat label="Within ±10%" value={data?.within_10pct_share != null ? `${data.within_10pct_share}%` : '—'} color="text-emerald-300" sub="share of forecasts ≥90% accurate" testId="fg-stat-within10" />
              <Stat label="$ graded" value={data ? `$${Math.round(data.total_actual_monthly_usd).toLocaleString()}/mo` : '—'} color="text-emerald-300" sub={`vs $${Math.round(data?.total_predicted_monthly_usd || 0).toLocaleString()}/mo predicted`} testId="fg-stat-dollars" />
            </div>
          </>
        )}

        <Card className="bg-slate-900/50 backdrop-blur-xl border-white/10 mb-8">
          <CardContent className="p-6" data-testid="fg-methodology">
            <h2 className="text-base md:text-lg font-bold text-white flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Methodology — nothing up our sleeve
            </h2>
            <ul className="text-sm text-slate-300 space-y-1.5 list-disc pl-5">
              <li>Accuracy = 100 − |actual − predicted| ÷ |predicted| × 100, floored at 0, per decision.</li>
              <li>Only <em>approved</em> decisions where the customer recorded a real-world outcome are counted.</li>
              <li>Aggregated across every CARTIEAI workspace. Anonymized by design — no customer names, no decision titles, no per-decision amounts ever leave a workspace.</li>
              <li>Demo-workspace and internal test decisions are excluded — the number is customer-only.</li>
              <li>We cannot edit or exclude individual grades. Bad forecasts count exactly like good ones.</li>
            </ul>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/decision-ledger" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 px-5 py-2.5 text-sm font-bold" data-testid="fg-cta-ledger">
            <Scale className="w-4 h-4" /> Open the Decision Ledger <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/honest-scorecard" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-5 py-2.5 text-sm font-semibold" data-testid="fg-cta-scorecard">
            See the Honest Scorecard
          </Link>
        </div>
      </main>
    </div>
  );
};

export default ForecastsGraded;
